/**
 * Report Presets — Pre-built report template definitions for the 6 standard financial reports.
 *
 * Each factory function returns a complete { definition, nodes, columnConfig } tuple
 * ready to load into the designer store.
 */
import { createDefaultLevelConfig } from '../utils/column-config-utils';
import { generateRowId } from '../utils/row-id-utils';
// ============================================================
// Shared helpers
// ============================================================
function uid() {
    return crypto.randomUUID();
}
function makeNode(rowId, data, depth, children = []) {
    const id = uid();
    return { id, rowId, depth, children, data: { ...data, id } };
}
function makeCols(defs) {
    return defs.map((d) => ({ ...d, id: uid() }));
}
const currency = (decimals = 2) => ({
    type: 'currency',
    decimals,
    negativeFormat: 'parentheses',
});
const percent = (decimals = 1) => ({
    type: 'percent',
    decimals,
});
const DEFAULT_THEME = {
    colors: {
        headerBackground: '#1e3a5f',
        sectionBackground: '#f0f4f8',
        subtotalBackground: '#e2e8f0',
    },
    negativeFormat: 'parentheses',
};
function baseDef(name, type, columns) {
    const now = new Date();
    return {
        id: uid(),
        name,
        type,
        version: 1,
        parameters: [
            { id: uid(), name: 'Company', type: 'company', required: true },
            { id: uid(), name: 'Period', type: 'period', required: true },
        ],
        columns,
        rows: [],
        theme: DEFAULT_THEME,
        metadata: { createdAt: now, updatedAt: now },
    };
}
// ============================================================
// 1. Trial Balance
// ============================================================
export function createTrialBalancePreset() {
    const columns = makeCols([
        { header: 'Account Code', type: 'account_code', width: 12 },
        { header: 'Account', type: 'label', width: 35 },
        { header: 'Debit', type: 'current', format: currency() },
        { header: 'Credit', type: 'current', format: currency() },
        { header: 'Net Balance', type: 'current', format: currency() },
    ]);
    const definition = baseDef('Trial Balance', 'trial_balance', columns);
    const existingRowIds = [];
    const nextId = () => {
        const id = generateRowId(existingRowIds);
        existingRowIds.push(id);
        return id;
    };
    // Title block
    const titleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: '{Company}', alignment: 'center', fontSize: 14, bold: true,
    }, 0);
    const subtitleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: 'Trial Balance — {Period}', alignment: 'center', fontSize: 12, bold: false, italic: true,
    }, 0);
    // Sections by account type
    const assetSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Assets',
        filter: { types: ['asset'] },
        showSubtotal: true, subtotalLabel: 'Total Assets',
    }, 0);
    const liabilitySection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Liabilities',
        filter: { types: ['liability'] },
        showSubtotal: true, subtotalLabel: 'Total Liabilities',
    }, 0);
    const equitySection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Equity',
        filter: { types: ['equity'] },
        showSubtotal: true, subtotalLabel: 'Total Equity',
    }, 0);
    const revenueSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Revenue',
        filter: { types: ['revenue'] },
        showSubtotal: true, subtotalLabel: 'Total Revenue',
    }, 0);
    const expenseSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Expenses',
        filter: { types: ['expense'] },
        showSubtotal: true, subtotalLabel: 'Total Expenses',
    }, 0);
    const line = makeNode(nextId(), {
        type: 'line', id: '', borderStyle: 'double',
    }, 0);
    const grandTotal = makeNode(nextId(), {
        type: 'grand_total', id: '', label: 'Grand Total',
        sumOf: [assetSection.rowId, liabilitySection.rowId, equitySection.rowId, revenueSection.rowId, expenseSection.rowId],
    }, 0);
    const nodes = [titleNode, subtitleNode, assetSection, liabilitySection, equitySection, revenueSection, expenseSection, line, grandTotal];
    const columnConfig = createDefaultLevelConfig(columns);
    const instructionText = [
        'TRIAL BALANCE TEMPLATE — Instructions',
        '',
        'This template lists all accounts grouped by type (Assets, Liabilities, Equity, Revenue, Expenses).',
        '',
        'HOW IT WORKS:',
        '- The Parameters sheet controls Company, Period, and Connection.',
        '- Each section uses an account type filter to pull matching accounts from your ERP.',
        '- Subtotals are automatically calculated for each section.',
        '- The Grand Total sums all section subtotals.',
        '',
        'CLM FUNCTIONS USED:',
        '- CLM.GL_BALANCE(account, asOfDate, companyId, connection) — retrieves the balance for each account.',
        '',
        'CUSTOMIZATION:',
        '- To add a specific account manually, drag an "Account" row from the palette.',
        '- To exclude accounts, edit the section filter in the Property Panel.',
        '- To add prior period comparison, use Column Definitions to add a "Prior Period" column.',
    ].join('\n');
    return { definition, nodes, columnConfig, instructionText };
}
// ============================================================
// 2. Income Statement
// ============================================================
export function createIncomeStatementPreset() {
    const currentCol = { header: 'Current Period', type: 'current', format: currency() };
    const priorCol = { header: 'Prior Period', type: 'prior_period', format: currency() };
    const varAmtCol = { header: 'Variance $', type: 'variance_amount', format: currency() };
    const varPctCol = { header: 'Variance %', type: 'variance_percent', format: percent() };
    const columns = makeCols([
        { header: 'Account', type: 'label', width: 35 },
        currentCol,
        priorCol,
        varAmtCol,
        varPctCol,
    ]);
    // Wire up variance column references
    const currentId = columns[1].id;
    const priorId = columns[2].id;
    columns[3].baseColumnId = currentId;
    columns[3].compareColumnId = priorId;
    columns[4].baseColumnId = currentId;
    columns[4].compareColumnId = priorId;
    const definition = baseDef('Income Statement', 'income_statement', columns);
    const existingRowIds = [];
    const nextId = () => {
        const id = generateRowId(existingRowIds);
        existingRowIds.push(id);
        return id;
    };
    const titleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: '{Company}', alignment: 'center', fontSize: 14, bold: true,
    }, 0);
    const subtitleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: 'Income Statement — {Period}', alignment: 'center', fontSize: 12, bold: false, italic: true,
    }, 0);
    const spacer1 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Revenue section
    const revenueHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Revenue', level: 1,
    }, 0);
    const revenueSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Revenue',
        filter: { types: ['revenue'] },
        showSubtotal: true, subtotalLabel: 'Total Revenue',
    }, 0);
    const spacer2 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Expense section
    const expenseHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Expenses', level: 1,
    }, 0);
    const expenseSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Expenses',
        filter: { types: ['expense'] },
        showSubtotal: true, subtotalLabel: 'Total Expenses',
    }, 0);
    const line = makeNode(nextId(), { type: 'line', id: '', borderStyle: 'double' }, 0);
    // Net Income = Revenue - Expenses (calculated row referencing subtotals)
    const netIncome = makeNode(nextId(), {
        type: 'calculated', id: '', label: 'Net Income',
        formula: `{${revenueSection.rowId}}-{${expenseSection.rowId}}`,
        style: 'bold',
    }, 0);
    const nodes = [titleNode, subtitleNode, spacer1, revenueHeader, revenueSection, spacer2, expenseHeader, expenseSection, line, netIncome];
    const columnConfig = createDefaultLevelConfig(columns);
    const instructionText = [
        'INCOME STATEMENT TEMPLATE — Instructions',
        '',
        'This template shows Revenue minus Expenses to calculate Net Income,',
        'with Current Period vs Prior Period variance columns.',
        '',
        'HOW IT WORKS:',
        '- Revenue accounts are pulled by account type filter (type = "revenue").',
        '- Expense accounts are pulled by account type filter (type = "expense").',
        '- Net Income is a calculated row: Total Revenue - Total Expenses.',
        '- Variance $ and Variance % compare Current vs Prior period automatically.',
        '',
        'CLM FUNCTIONS USED:',
        '- CLM.GL_BALANCE(account, periodEnd, companyId, connection) — current period balance.',
        '- CLM.GL_BALANCE(account, EOMONTH(periodEnd, -1), ...) — prior period balance.',
        '',
        'CUSTOMIZATION:',
        '- Add sub-groups by creating additional Section rows with narrower filters.',
        '- Add a Budget column via Column Definitions for budget-to-actual comparison.',
        '- Change the variance base/compare columns in Column Definitions.',
    ].join('\n');
    return { definition, nodes, columnConfig, instructionText };
}
// ============================================================
// 3. Balance Sheet
// ============================================================
export function createBalanceSheetPreset() {
    const columns = makeCols([
        { header: 'Account', type: 'label', width: 35 },
        { header: 'Current Period', type: 'current', format: currency() },
        { header: 'Prior Year', type: 'prior_year', format: currency() },
        { header: 'Change $', type: 'variance_amount', format: currency() },
        { header: 'Change %', type: 'variance_percent', format: percent() },
    ]);
    // Wire variance references
    const currentId = columns[1].id;
    const priorYearId = columns[2].id;
    columns[3].baseColumnId = currentId;
    columns[3].compareColumnId = priorYearId;
    columns[4].baseColumnId = currentId;
    columns[4].compareColumnId = priorYearId;
    const definition = baseDef('Balance Sheet', 'balance_sheet', columns);
    const existingRowIds = [];
    const nextId = () => {
        const id = generateRowId(existingRowIds);
        existingRowIds.push(id);
        return id;
    };
    const titleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: '{Company}', alignment: 'center', fontSize: 14, bold: true,
    }, 0);
    const subtitleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: 'Balance Sheet — {Period}', alignment: 'center', fontSize: 12, bold: false, italic: true,
    }, 0);
    const spacer1 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Assets
    const assetsHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Assets', level: 1,
    }, 0);
    const assetSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Assets',
        filter: { types: ['asset'] },
        showSubtotal: true, subtotalLabel: 'Total Assets',
    }, 0);
    const spacer2 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Liabilities
    const liabilitiesHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Liabilities', level: 1,
    }, 0);
    const liabilitySection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Liabilities',
        filter: { types: ['liability'] },
        showSubtotal: true, subtotalLabel: 'Total Liabilities',
    }, 0);
    const spacer3 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Equity
    const equityHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Equity', level: 1,
    }, 0);
    const equitySection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Equity',
        filter: { types: ['equity'] },
        showSubtotal: true, subtotalLabel: 'Total Equity',
    }, 0);
    const spacer4 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Total Liabilities + Equity
    const totalLiabEquity = makeNode(nextId(), {
        type: 'calculated', id: '', label: 'Total Liabilities & Equity',
        formula: `{${liabilitySection.rowId}}+{${equitySection.rowId}}`,
        style: 'bold',
    }, 0);
    const line = makeNode(nextId(), { type: 'line', id: '', borderStyle: 'double' }, 0);
    const nodes = [
        titleNode, subtitleNode, spacer1,
        assetsHeader, assetSection,
        spacer2,
        liabilitiesHeader, liabilitySection,
        spacer3,
        equityHeader, equitySection,
        spacer4,
        totalLiabEquity,
        line,
    ];
    const columnConfig = createDefaultLevelConfig(columns);
    const instructionText = [
        'BALANCE SHEET TEMPLATE — Instructions',
        '',
        'This template shows Assets, Liabilities, and Equity with Current Period vs Prior Year comparison.',
        '',
        'HOW IT WORKS:',
        '- Three sections filter accounts by type: asset, liability, equity.',
        '- Each section shows all matching accounts with automatic subtotals.',
        '- "Total Liabilities & Equity" is a calculated row summing the L + E subtotals.',
        '- Change $ and Change % compare Current Period to Prior Year (same date, year - 1).',
        '',
        'CLM FUNCTIONS USED:',
        '- CLM.GL_BALANCE(account, periodEnd, companyId, connection) — current balance.',
        '- CLM.GL_BALANCE(account, DATE(YEAR(periodEnd)-1, ...), ...) — prior year balance.',
        '',
        'CUSTOMIZATION:',
        '- Split Assets into Current Assets and Non-Current Assets with sub-sections.',
        '- Add an "Account Code" column via Column Definitions for reference.',
        '- Add YTD column to show year-to-date movement.',
    ].join('\n');
    return { definition, nodes, columnConfig, instructionText };
}
// ============================================================
// 4. Cash Flow Statement (Premium)
// ============================================================
export function createCashFlowPreset() {
    const columns = makeCols([
        { header: 'Description', type: 'label', width: 40 },
        { header: 'Current Period', type: 'current', format: currency() },
        { header: 'Prior Period', type: 'prior_period', format: currency() },
    ]);
    const definition = baseDef('Cash Flow Statement', 'cash_flow', columns);
    const existingRowIds = [];
    const nextId = () => {
        const id = generateRowId(existingRowIds);
        existingRowIds.push(id);
        return id;
    };
    const titleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: '{Company}', alignment: 'center', fontSize: 14, bold: true,
    }, 0);
    const subtitleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: 'Statement of Cash Flows — {Period}', alignment: 'center', fontSize: 12, bold: false, italic: true,
    }, 0);
    const spacer1 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Operating Activities
    const opHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Cash Flows from Operating Activities', level: 1,
    }, 0);
    // Placeholder accounts — user must configure with their actual account codes
    const netIncomeRow = makeNode(nextId(), {
        type: 'account', id: '', accountCode: '999999', label: 'Net Income (replace with actual account)',
    }, 0);
    const depreciationRow = makeNode(nextId(), {
        type: 'account', id: '', accountCode: '999998', label: 'Depreciation & Amortization (replace with actual account)',
    }, 0);
    const arRow = makeNode(nextId(), {
        type: 'account', id: '', accountCode: '999997', label: 'Change in Accounts Receivable (replace with actual account)',
    }, 0);
    const apRow = makeNode(nextId(), {
        type: 'account', id: '', accountCode: '999996', label: 'Change in Accounts Payable (replace with actual account)',
    }, 0);
    const opSubtotal = makeNode(nextId(), {
        type: 'subtotal', id: '', label: 'Net Cash from Operating Activities',
        sumOf: [netIncomeRow.rowId, depreciationRow.rowId, arRow.rowId, apRow.rowId],
        style: 'bold',
    }, 0);
    const spacer2 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Investing Activities
    const invHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Cash Flows from Investing Activities', level: 1,
    }, 0);
    const capexRow = makeNode(nextId(), {
        type: 'account', id: '', accountCode: '999995', label: 'Capital Expenditures (replace with actual account)',
    }, 0);
    const invSubtotal = makeNode(nextId(), {
        type: 'subtotal', id: '', label: 'Net Cash from Investing Activities',
        sumOf: [capexRow.rowId],
        style: 'bold',
    }, 0);
    const spacer3 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Financing Activities
    const finHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Cash Flows from Financing Activities', level: 1,
    }, 0);
    const debtRow = makeNode(nextId(), {
        type: 'account', id: '', accountCode: '999994', label: 'Debt Proceeds / Repayments (replace with actual account)',
    }, 0);
    const dividendRow = makeNode(nextId(), {
        type: 'account', id: '', accountCode: '999993', label: 'Dividends Paid (replace with actual account)',
    }, 0);
    const finSubtotal = makeNode(nextId(), {
        type: 'subtotal', id: '', label: 'Net Cash from Financing Activities',
        sumOf: [debtRow.rowId, dividendRow.rowId],
        style: 'bold',
    }, 0);
    const line = makeNode(nextId(), { type: 'line', id: '', borderStyle: 'double' }, 0);
    const netChange = makeNode(nextId(), {
        type: 'calculated', id: '', label: 'Net Change in Cash',
        formula: `{${opSubtotal.rowId}}+{${invSubtotal.rowId}}+{${finSubtotal.rowId}}`,
        style: 'bold',
    }, 0);
    const nodes = [
        titleNode, subtitleNode, spacer1,
        opHeader, netIncomeRow, depreciationRow, arRow, apRow, opSubtotal,
        spacer2,
        invHeader, capexRow, invSubtotal,
        spacer3,
        finHeader, debtRow, dividendRow, finSubtotal,
        line, netChange,
    ];
    const columnConfig = createDefaultLevelConfig(columns);
    const instructionText = [
        'CASH FLOW STATEMENT TEMPLATE — Instructions',
        '',
        'This template provides a starting framework for the Statement of Cash Flows.',
        'It uses PLACEHOLDER accounts (999999, 999998, etc.) that you must replace with',
        'your actual account codes from your ERP chart of accounts.',
        '',
        'IMPORTANT: This template requires manual configuration.',
        '',
        'HOW IT WORKS:',
        '- Three sections: Operating, Investing, and Financing activities.',
        '- Each section has placeholder account rows that you replace with real account codes.',
        '- Subtotals sum each section. Net Change in Cash sums all three sections.',
        '',
        'TO CUSTOMIZE:',
        '1. Click on each placeholder account row in the designer.',
        '2. In the Property Panel, change the Account Code to your actual account.',
        '3. Update the Label to match your chart of accounts description.',
        '4. Add or remove rows as needed for your specific cash flow structure.',
        '',
        'CLM FUNCTIONS USED:',
        '- CLM.GL_BALANCE — retrieves the balance for each account line.',
        '',
        'NOTE: For proper cash flow reporting, you may need to use CLM.GL_SUM for',
        'period movement amounts rather than point-in-time balances. Adjust the',
        'column type to "YTD" or use calculated columns as needed.',
    ].join('\n');
    return { definition, nodes, columnConfig, instructionText };
}
// ============================================================
// 5. Variance Analysis (Premium)
// ============================================================
export function createVarianceAnalysisPreset() {
    const columns = makeCols([
        { header: 'Account', type: 'label', width: 35 },
        { header: 'Actual', type: 'current', format: currency() },
        { header: 'Budget', type: 'budget', format: currency(), budgetName: '2026 Budget' },
        { header: 'Variance $', type: 'variance_amount', format: currency() },
        { header: 'Variance %', type: 'variance_percent', format: percent() },
        { header: 'Prior Year', type: 'prior_year', format: currency() },
    ]);
    // Wire budget variance references
    const actualId = columns[1].id;
    const budgetId = columns[2].id;
    columns[3].baseColumnId = actualId;
    columns[3].compareColumnId = budgetId;
    columns[4].baseColumnId = actualId;
    columns[4].compareColumnId = budgetId;
    const definition = baseDef('Variance Analysis', 'budget_vs_actual', columns);
    const existingRowIds = [];
    const nextId = () => {
        const id = generateRowId(existingRowIds);
        existingRowIds.push(id);
        return id;
    };
    const titleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: '{Company}', alignment: 'center', fontSize: 14, bold: true,
    }, 0);
    const subtitleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: 'Budget vs Actual Variance Analysis — {Period}', alignment: 'center', fontSize: 12, bold: false, italic: true,
    }, 0);
    const spacer1 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Revenue
    const revenueHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Revenue', level: 1,
    }, 0);
    const revenueSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Revenue',
        filter: { types: ['revenue'] },
        showSubtotal: true, subtotalLabel: 'Total Revenue',
    }, 0);
    const spacer2 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Expenses
    const expenseHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Expenses', level: 1,
    }, 0);
    const expenseSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Expenses',
        filter: { types: ['expense'] },
        showSubtotal: true, subtotalLabel: 'Total Expenses',
    }, 0);
    const line = makeNode(nextId(), { type: 'line', id: '', borderStyle: 'double' }, 0);
    const netIncome = makeNode(nextId(), {
        type: 'calculated', id: '', label: 'Net Income',
        formula: `{${revenueSection.rowId}}-{${expenseSection.rowId}}`,
        style: 'bold',
    }, 0);
    const nodes = [
        titleNode, subtitleNode, spacer1,
        revenueHeader, revenueSection,
        spacer2,
        expenseHeader, expenseSection,
        line, netIncome,
    ];
    const columnConfig = createDefaultLevelConfig(columns);
    const instructionText = [
        'VARIANCE ANALYSIS TEMPLATE — Instructions',
        '',
        'This template compares Actual results to Budget with variance calculations,',
        'plus Prior Year for additional context.',
        '',
        'HOW IT WORKS:',
        '- Actual column uses CLM.GL_BALANCE for current period balances.',
        '- Budget column uses CLM.BUDGET_AMOUNT referencing the "2026 Budget".',
        '- Variance $ = Actual - Budget (auto-calculated).',
        '- Variance % = (Actual - Budget) / |Budget| (auto-calculated, blank on subtotals).',
        '- Prior Year column provides year-over-year context.',
        '',
        'CLM FUNCTIONS USED:',
        '- CLM.GL_BALANCE — current period and prior year balances.',
        '- CLM.BUDGET_AMOUNT — budget amounts for the period.',
        '',
        'IMPORTANT:',
        '- The Budget column references "2026 Budget" by default.',
        '- To change the budget name, open Column Definitions and edit the Budget Name field.',
        '- You can list available budgets using CLM.BUDGET_LIST in Excel.',
        '',
        'CUSTOMIZATION:',
        '- Add additional sections for more granular analysis.',
        '- Add a "YTD" column for year-to-date comparisons.',
        '- Change variance base/compare columns in Column Definitions.',
    ].join('\n');
    return { definition, nodes, columnConfig, instructionText };
}
// ============================================================
// 6. Consolidated Report (Elite)
// ============================================================
export function createConsolidatedPreset() {
    const columns = makeCols([
        { header: 'Account', type: 'label', width: 35 },
        { header: 'Current Period', type: 'current', format: currency() },
        { header: 'Prior Year', type: 'prior_year', format: currency() },
        { header: 'Change $', type: 'variance_amount', format: currency() },
        { header: 'Change %', type: 'variance_percent', format: percent() },
    ]);
    // Wire variance references
    const currentId = columns[1].id;
    const priorYearId = columns[2].id;
    columns[3].baseColumnId = currentId;
    columns[3].compareColumnId = priorYearId;
    columns[4].baseColumnId = currentId;
    columns[4].compareColumnId = priorYearId;
    const definition = baseDef('Consolidated Financial Report', 'custom', columns);
    const existingRowIds = [];
    const nextId = () => {
        const id = generateRowId(existingRowIds);
        existingRowIds.push(id);
        return id;
    };
    const titleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: '{Company}', alignment: 'center', fontSize: 14, bold: true,
    }, 0);
    const subtitleNode = makeNode(nextId(), {
        type: 'title_block', id: '', text: 'Consolidated Financial Report — {Period}', alignment: 'center', fontSize: 12, bold: false, italic: true,
    }, 0);
    const spacer1 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Assets
    const assetsHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Assets', level: 1,
    }, 0);
    const assetSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Assets',
        filter: { types: ['asset'] },
        showSubtotal: true, subtotalLabel: 'Total Assets',
    }, 0);
    const spacer2 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Liabilities
    const liabilitiesHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Liabilities', level: 1,
    }, 0);
    const liabilitySection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Liabilities',
        filter: { types: ['liability'] },
        showSubtotal: true, subtotalLabel: 'Total Liabilities',
    }, 0);
    const spacer3 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Equity
    const equityHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Equity', level: 1,
    }, 0);
    const equitySection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Equity',
        filter: { types: ['equity'] },
        showSubtotal: true, subtotalLabel: 'Total Equity',
    }, 0);
    const spacer4 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    // Revenue & Expenses
    const revenueHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Revenue', level: 1,
    }, 0);
    const revenueSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Revenue',
        filter: { types: ['revenue'] },
        showSubtotal: true, subtotalLabel: 'Total Revenue',
    }, 0);
    const spacer5 = makeNode(nextId(), { type: 'spacer', id: '' }, 0);
    const expenseHeader = makeNode(nextId(), {
        type: 'header', id: '', text: 'Expenses', level: 1,
    }, 0);
    const expenseSection = makeNode(nextId(), {
        type: 'section', id: '', name: 'Expenses',
        filter: { types: ['expense'] },
        showSubtotal: true, subtotalLabel: 'Total Expenses',
    }, 0);
    const line = makeNode(nextId(), { type: 'line', id: '', borderStyle: 'double' }, 0);
    const netIncome = makeNode(nextId(), {
        type: 'calculated', id: '', label: 'Net Income',
        formula: `{${revenueSection.rowId}}-{${expenseSection.rowId}}`,
        style: 'bold',
    }, 0);
    const nodes = [
        titleNode, subtitleNode, spacer1,
        assetsHeader, assetSection,
        spacer2,
        liabilitiesHeader, liabilitySection,
        spacer3,
        equityHeader, equitySection,
        spacer4,
        revenueHeader, revenueSection,
        spacer5,
        expenseHeader, expenseSection,
        line, netIncome,
    ];
    const columnConfig = createDefaultLevelConfig(columns);
    const instructionText = [
        'CONSOLIDATED FINANCIAL REPORT TEMPLATE — Instructions',
        '',
        'This template provides a comprehensive view across all account types,',
        'suitable for consolidated multi-company reporting.',
        '',
        'HOW IT WORKS:',
        '- Sections for Assets, Liabilities, Equity, Revenue, and Expenses.',
        '- Current Period vs Prior Year comparison with change $ and %.',
        '- Net Income calculated as Revenue - Expenses.',
        '',
        'FOR MULTI-COMPANY CONSOLIDATION:',
        '- Use the CLM.CONSOL_BALANCE function instead of CLM.GL_BALANCE.',
        '- This sums balances across all companies in the selected connection.',
        '- To switch to consolidated mode, you will need to customize the column',
        '  type or use calculated columns with CONSOL functions.',
        '',
        'CLM FUNCTIONS FOR CONSOLIDATION:',
        '- CLM.CONSOL_BALANCE(account, date, connection) — sum across all companies.',
        '- CLM.CONSOL_DEBIT(account, date, connection) — total debits across companies.',
        '- CLM.CONSOL_CREDIT(account, date, connection) — total credits across companies.',
        '',
        'CUSTOMIZATION:',
        '- Add inter-company elimination rows as needed.',
        '- Add per-company columns using the "Calculated" column type.',
        '- Adjust filters for entity-specific account groupings.',
    ].join('\n');
    return { definition, nodes, columnConfig, instructionText };
}
// ============================================================
// Preset registry
// ============================================================
export const REPORT_PRESETS = {
    trial_balance: createTrialBalancePreset,
    income_statement: createIncomeStatementPreset,
    balance_sheet: createBalanceSheetPreset,
    cash_flow: createCashFlowPreset,
    variance_analysis: createVarianceAnalysisPreset,
    consolidated: createConsolidatedPreset,
};
export function getPreset(presetId) {
    const factory = REPORT_PRESETS[presetId];
    if (!factory)
        return null;
    return factory();
}
//# sourceMappingURL=report-presets.js.map